import { ASKED_FLAGS, DERIVED_FLAGS, FLAG_GROUP_TITLES, type FlagGroup } from '@/data/applicationFlags'
import type { AppFlagKey, ApplicationAnswers, ApplicationCreationConfig } from '@/types/application'
import styles from './Infrastructure.module.css'
import { CheckboxField } from './CheckboxField'
import { ReadOnlyInfoCard } from './ReadOnlyInfoCard'

interface ApplicationCreationProps {
  answers: ApplicationAnswers
  onChange: (answers: ApplicationAnswers) => void
  /** The script's first seven inputs, all taken from earlier answers. */
  inputs: Omit<ApplicationCreationConfig, 'flags'>
  /** Every flag's value; used for the ones that are not asked. */
  flags: Record<AppFlagKey, boolean>
}

const none = (value: string) => (value === '' ? '—' : value)
const GROUPS: readonly FlagGroup[] = ['core', 'other']

/**
 * Application creation: the inputs of the Traverse set-up script (New-TraverseEnvironment.ps1). Its first seven are taken from the
 * earlier answers and shown read-only. Its optional Y/N flags (each N unless ticked) are asked, except the ones an earlier answer already
 * gives: the add-ons chosen on the Infrastructure step, and Use Master Login in License management.
 */
export function ApplicationCreation({ answers, onChange, inputs, flags }: ApplicationCreationProps) {
  const toggle = (key: AppFlagKey, checked: boolean) => onChange({ ...answers, flags: { ...answers.flags, [key]: checked } })

  return (
    <section className={styles.section} aria-label="Application creation" data-testid="application-creation">
      <h4 className={styles.sectionTitle}>Application creation</h4>
      <p className={styles.note}>The inputs of the Traverse set-up script (New-TraverseEnvironment.ps1).</p>

      <ReadOnlyInfoCard
        title="From your earlier answers"
        items={[
          { label: 'CustomerName', value: none(inputs.CustomerName), mono: true },
          { label: 'ProductLine', value: none(inputs.ProductLine), mono: true },
          { label: 'RDSServer', value: none(inputs.RDSServer), mono: true },
          { label: 'TLSPort', value: none(inputs.TLSPort), mono: true },
          { label: 'SYSDB', value: none(inputs.SYSDB), mono: true },
          { label: 'CompID', value: none(inputs.CompID), mono: true },
          { label: 'EnvType', value: inputs.EnvType, mono: true },
        ]}
        note="Not asked again: these are the answers you gave on the earlier steps."
      />

      {GROUPS.map((group) => {
        const asked = ASKED_FLAGS.filter((flag) => flag.group === group)
        return (
          <fieldset key={group} className={styles.cardGroup} style={{ display: 'block' }}>
            <legend className={styles.legend}>{FLAG_GROUP_TITLES[group]} (optional, each is No unless ticked)</legend>
            {asked.map((flag) => (
              <CheckboxField key={flag.key} label={flag.label} description={flag.key} checked={answers.flags[flag.key] === true} onChange={(checked) => toggle(flag.key, checked)} />
            ))}
          </fieldset>
        )
      })}

      <ReadOnlyInfoCard
        title="Also from your earlier answers"
        items={DERIVED_FLAGS.map((flag) => ({ label: `${flag.label} (${flag.key})`, value: flags[flag.key] ? 'Y' : 'N', mono: true }))}
        note="Not asked again. The add-ons come from the Infrastructure step, and the master login from License management."
      />
    </section>
  )
}
